import { useCallback, useEffect, useRef, useState } from 'react'
import { App } from 'antd'
import { Team } from '@sportninja/common/types/TeamStanding'
import { Post } from '@sportninja/common/hooks/useSocialFeed'
import req from '@sportninja/common/api/request'
import { useDebounce } from '@sportninja/common/hooks/useDebounce'
import { OrganizationTreeNode } from 'src/context/MyScheduleContext/types'

const TEAMS_PER_PAGE = 25

interface UseEntityDataProps {
  entity?: any
  isOpen: boolean
  postToEdit: Post | null
  teamsSearchQuery?: string
}

interface UseEntityDataReturn {
  orgTree: OrganizationTreeNode[]
  teams: Team[]
  isLoadingEntities: boolean
  hasMoreTeams: boolean
  isLoadingMoreTeams: boolean
  loadMoreTeams: () => void
}

/**
 * Recursively filters sandbox entities from the org tree.
 * Removes org nodes whose name_full includes 'Sandbox' and
 * filters out sandbox schedules within each node.
 */
const filterTreeSandbox = (
  nodes: OrganizationTreeNode[]
): OrganizationTreeNode[] => {
  return nodes
    .filter((node) => !node.name_full?.includes('Sandbox'))
    .map((node) => ({
      ...node,
      children: node.children ? filterTreeSandbox(node.children) : undefined,
      schedules: node.schedules?.filter(
        (s) => !s.name_full?.includes('Sandbox')
      ),
    }))
}

const buildTeamsUrl = (page: number, query?: string): string => {
  let url = `/social-feed/teams?sort=name&direction=asc&membership_type=permission&recursive=1&per_page=${TEAMS_PER_PAGE}&page=${page}`
  if (query && query.trim().length > 0) {
    url += `&query=${encodeURIComponent(query.trim())}`
  }
  return url
}

/**
 * Custom hook for fetching entity data using the organizations tree endpoint
 * and a flat teams list with pagination and API-driven search.
 *
 * Uses GET /v1/social-feed/permission-tree for orgs + schedules (hierarchical),
 * and /v1/social-feed/teams for flat teams with pagination.
 */
export const useEntityData = ({
  entity,
  isOpen,
  postToEdit: _postToEdit,
  teamsSearchQuery = '',
}: UseEntityDataProps): UseEntityDataReturn => {
  const [orgTree, setOrgTree] = useState<OrganizationTreeNode[]>([])
  const [teams, setTeams] = useState<Team[]>([])
  const [isLoadingEntities, setIsLoadingEntities] = useState(false)
  const [teamsPage, setTeamsPage] = useState(1)
  const [teamsTotalPages, setTeamsTotalPages] = useState(1)
  const [isLoadingMoreTeams, setIsLoadingMoreTeams] = useState(false)

  const debouncedSearchQuery = useDebounce(teamsSearchQuery, 500)
  const loadingMoreRef = useRef(false)
  const initialLoadDone = useRef(false)
  const [openCount, setOpenCount] = useState(0)

  const { message } = App.useApp()

  const hasMoreTeams = teamsPage < teamsTotalPages

  // Reset all state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setOrgTree([])
      setTeams([])
      setTeamsPage(1)
      setTeamsTotalPages(1)
      initialLoadDone.current = false
    }
  }, [isOpen])

  // Increment openCount each time modal opens to force refetch
  useEffect(() => {
    if (isOpen) {
      setOpenCount((c) => c + 1)
    }
  }, [isOpen])

  const fetchTeams = useCallback(
    async (
      page: number,
      query?: string
    ): Promise<{ data: Team[]; totalPages: number }> => {
      const response = await req(buildTeamsUrl(page, query))
      const data = (response?.data || []).filter(
        (item: any) => !item.name_full?.includes('Sandbox')
      )
      const totalPages = response?.meta?.pagination?.total_pages || 1
      return { data, totalPages }
    },
    []
  )

  // Initial load: org tree + teams page 1
  const loadEntitiesData = useCallback(async () => {
    if (entity || !isOpen) {
      return
    }

    try {
      setIsLoadingEntities(true)

      const [treeResponse, teamsResult] = await Promise.all([
        req(
          '/social-feed/permission-tree?include_schedules=true&membership_type=permission'
        ),
        fetchTeams(1),
      ])

      const treeData: OrganizationTreeNode[] = treeResponse?.data || []
      setOrgTree(filterTreeSandbox(treeData))
      setTeams(teamsResult.data)
      setTeamsPage(1)
      setTeamsTotalPages(teamsResult.totalPages)
      initialLoadDone.current = true
    } catch (error) {
      message.error('Failed to load organizations and teams')
    } finally {
      setIsLoadingEntities(false)
    }
  }, [entity, isOpen, message, fetchTeams, openCount])

  useEffect(() => {
    loadEntitiesData()
  }, [loadEntitiesData])

  // Re-fetch teams when debounced search query changes
  useEffect(() => {
    if (entity || !isOpen) {
      return
    }
    // Skip before initial load completes — loadEntitiesData handles the first fetch
    if (!initialLoadDone.current) {
      return
    }

    let cancelled = false

    const searchTeams = async () => {
      setIsLoadingMoreTeams(true)
      try {
        const result = await fetchTeams(1, debouncedSearchQuery || undefined)
        if (!cancelled) {
          setTeams(result.data)
          setTeamsPage(1)
          setTeamsTotalPages(result.totalPages)
        }
      } catch (_error) {
        // Silently fail for search
      } finally {
        if (!cancelled) {
          setIsLoadingMoreTeams(false)
        }
      }
    }

    searchTeams()

    return () => {
      cancelled = true
    }
  }, [debouncedSearchQuery]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadMoreTeams = useCallback(() => {
    if (!hasMoreTeams || loadingMoreRef.current) {
      return
    }
    loadingMoreRef.current = true
    setIsLoadingMoreTeams(true)

    const nextPage = teamsPage + 1

    fetchTeams(nextPage, debouncedSearchQuery || undefined)
      .then((result) => {
        setTeams((prev) => [...prev, ...result.data])
        setTeamsPage(nextPage)
        setTeamsTotalPages(result.totalPages)
      })
      .catch(() => {
        // Silently fail
      })
      .finally(() => {
        setIsLoadingMoreTeams(false)
        loadingMoreRef.current = false
      })
  }, [hasMoreTeams, teamsPage, fetchTeams, debouncedSearchQuery])

  return {
    orgTree,
    teams,
    isLoadingEntities,
    hasMoreTeams,
    isLoadingMoreTeams,
    loadMoreTeams,
  }
}
